import React, { useState, useEffect } from 'react';
import { Heart, Eye, EyeOff, Star, Calendar, Users, Clock, Search, BarChart3, X, Film, Play, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getFavorites, getWatchedMovies, getUserStats, addToFavorites, markAsWatched, markAsUnwatched, removeFromFavorites, isFavorite, isWatched, registerGlobalRefresh, fixFavoriteDataConsistency } from '../services/favoriteService';
import { getMoviePoster, getMovieTrailer, getMovieDetailsWithCast } from '../services/tmdbService';
import TrailerModal from '../components/TrailerModal';
import ConfirmationModal from '../components/ConfirmationModal';

const FavoritesPage = () => {
    // Görev 7: Auth kontrolü
    const { authStatus, user } = useAuth();
    const { t, formatDate } = useLanguage();
    
    const [favorites, setFavorites] = useState([]);
    const [watchedMovies, setWatchedMovies] = useState([]);
    const [stats, setStats] = useState({});
    const [activeTab, setActiveTab] = useState('favorites'); // 'favorites', 'watched', 'stats'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('');
    const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'title', 'rating'
    const [posterUrls, setPosterUrls] = useState({});
    const [refreshKey] = useState(0);
    const [selectedMovie, setSelectedMovie] = useState(null); // Detay modal için

    // Trailer modal state'leri
    const [selectedTrailer, setSelectedTrailer] = useState(null);
    const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false);
    const [trailerLoading, setTrailerLoading] = useState(false);
    const [currentMovieForTrailer, setCurrentMovieForTrailer] = useState('');

    // Confirmation modal state'leri
    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        onConfirm: null,
        confirmText: 'Evet',
        cancelText: 'İptal',
        isLoading: false
    });

    // Güvenli şekilde Google araması açar
    const openGoogleSearch = (title = '') => {
        const safeTitle = title.trim();
        if (!safeTitle) return;
        const q = encodeURIComponent(`${safeTitle} full izle`);
        window.open(`https://www.google.com/search?q=${q}`, "_blank", "noopener,noreferrer");
    };

    // Verileri yükle - sadece kullanıcı hazırken (Görev 7)
    useEffect(() => {
        // Auth hazır değilse bekle
        if (authStatus !== 'authenticated' || !user?.id) {
            return;
        }

        loadData();

        // Sayfa açıldığında veri tutarlılığını kontrol et
        const consistencyResult = fixFavoriteDataConsistency();
        if (consistencyResult && consistencyResult.removedDuplicates > 0) {
            // Duplicate düzeltildi (sessizce)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey, authStatus, user?.id]);

    // Global refresh sistemini kaydet (sonsuz döngü önleme ile)
    useEffect(() => {
        const unregister = registerGlobalRefresh(() => {
            // Sadece veri yükleme fonksiyonunu çağır, setRefreshKey kullanma
            loadData();
        });

        return unregister; // Cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = () => {
        try {
            const favoritesData = getFavorites();
            const watchedData = getWatchedMovies();
            const statsData = getUserStats();

            setFavorites(favoritesData);
            setWatchedMovies(watchedData);
            setStats(statsData);

            // Posterları yükle - Sadece posterUrl'i olmayan veya geçersiz olanları TMDB'den çek
            const allMovies = [...favoritesData, ...watchedData];
            allMovies.forEach(movie => {
                // Eğer movie.posterUrl geçerliyse, posterUrls state'ine direkt ekle (TMDB'den çekme)
                if (movie.posterUrl && 
                    movie.posterUrl !== '/placeholder-poster.jpg' &&
                    movie.posterUrl !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' &&
                    movie.posterUrl.startsWith('https://image.tmdb.org')) {
                    setPosterUrls(prev => ({
                        ...prev,
                        [movie.id]: movie.posterUrl
                    }));
                } else {
                    // Poster yoksa veya geçersizse, TMDB'den çek
                    loadPoster(movie);
                }
            });
        } catch (error) {
            console.error('❌ FavoritesPage: Veri yükleme hatası:', error);
        }
    };

    // Poster yükleme - ÖNCE movie.posterUrl'i kontrol et, yoksa TMDB'den çek
    const loadPoster = async (movie) => {
        // Eğer movie.posterUrl varsa ve geçerliyse, onu kullan (backend'den gelen doğru poster)
        if (movie.posterUrl && 
            movie.posterUrl !== '/placeholder-poster.jpg' &&
            movie.posterUrl !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' &&
            movie.posterUrl.startsWith('https://image.tmdb.org')) {
            setPosterUrls(prev => ({
                ...prev,
                [movie.id]: movie.posterUrl
            }));
            return; // Backend'den gelen doğru poster URL'ini kullan, TMDB'den tekrar çekme
        }

        // Poster yoksa veya geçersizse, TMDB'den çek (year ve contentType ile daha doğru sonuç)
        try {
            const contentType = movie.contentType || 'film';
            const posterUrl = await getMoviePoster(movie.title, movie.year, contentType);
            setPosterUrls(prev => ({
                ...prev,
                [movie.id]: posterUrl
            }));
        } catch (error) {
            console.error('Poster yükleme hatası:', error);
            // Hata durumunda mevcut posterUrl'i koru veya placeholder kullan
            if (movie.posterUrl) {
                setPosterUrls(prev => ({
                    ...prev,
                    [movie.id]: movie.posterUrl
                }));
            }
        }
    };



    // Favori durumu toggle
    const handleFavoriteToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyFavorite = isFavorite(movieId, movie.title);

        if (isCurrentlyFavorite) {
            // Favorilerden çıkarma onayı
            openConfirmationModal({
                title: t('favorites.removeFromFavoritesConfirm'),
                message: t('favorites.removeFromFavoritesMessage', { title: movie.title }),
                type: 'danger',
                confirmText: t('favorites.confirmRemove'),
                cancelText: t('common.cancel'),
                onConfirm: async () => {
                    const success = removeFromFavorites(movieId, movie.title);
                    if (!success) {
                        console.error('❌ Film favorilerden çıkarılamadı:', movie.title);
                    }
                }
            });
        } else {
            // Favorilere ekleme (onay gerektirmez)
            const success = addToFavorites(movie);
            if (!success) {
                console.error('❌ Film favorilere eklenemedi:', movie.title);
            }
        }
    };

    // İzleme durumu toggle
    const handleWatchedToggle = (movie) => {
        const movieId = movie.id;
        const isCurrentlyWatched = isWatched(movieId);

        if (isCurrentlyWatched) {
            // İzlenmedi olarak işaretleme onayı
            openConfirmationModal({
                title: t('favorites.markAsUnwatchedConfirm'),
                message: t('favorites.markAsUnwatchedMessage', { title: movie.title }),
                type: 'warning',
                confirmText: t('favorites.confirmUnwatched'),
                cancelText: t('common.cancel'),
                onConfirm: async () => {
                    const success = markAsUnwatched(movieId);
                    if (!success) {
                        console.error('❌ Film izlenmedi olarak işaretlenemedi:', movie.title);
                    }
                }
            });
        } else {
            // İzlendi olarak işaretleme (onay gerektirmez)
            const success = markAsWatched(movie);
            if (!success) {
                console.error('❌ Film izlendi olarak işaretlenemedi:', movie.title);
            }
        }
    };

    // Film kartına tıklama
    const handleMovieClick = (movie) => {
        setSelectedMovie(movie);
    };

    // Modal kapatma
    const closeModal = () => {
        setSelectedMovie(null);
    };

    // Fragman açma fonksiyonu
    const handleWatchTrailer = async (movie) => {
        try {
            setTrailerLoading(true);
            setCurrentMovieForTrailer(movie.title);

            const trailer = await getMovieTrailer(movie.title, movie.year);

            setSelectedTrailer(trailer);
            setIsTrailerModalOpen(true);

        } catch (error) {
            console.error('❌ Fragman yükleme hatası:', error);
            // Hata durumunda da modal aç (fragman bulunamadı mesajı için)
            setSelectedTrailer(null);
            setIsTrailerModalOpen(true);
        } finally {
            setTrailerLoading(false);
        }
    };

    // Trailer modal kapatma
    const closeTrailerModal = () => {
        setIsTrailerModalOpen(false);
        setSelectedTrailer(null);
        setCurrentMovieForTrailer('');
    };

    // Confirmation modal açma
    const openConfirmationModal = (config) => {
        setConfirmationModal({
            isOpen: true,
            ...config
        });
    };

    // Confirmation modal kapatma
    const closeConfirmationModal = () => {
        setConfirmationModal(prev => ({
            ...prev,
            isOpen: false,
            isLoading: false
        }));
    };

    // Confirmation modal onaylama
    const handleConfirmationConfirm = async () => {
        if (confirmationModal.onConfirm) {
            setConfirmationModal(prev => ({ ...prev, isLoading: true }));
            try {
                await confirmationModal.onConfirm();
                closeConfirmationModal();
            } catch (error) {
                console.error('Confirmation action failed:', error);
                setConfirmationModal(prev => ({ ...prev, isLoading: false }));
            }
        }
    };

    // Filtreleme ve sıralama
    const filterAndSortMovies = (movies) => {
        let filtered = movies;

        // Arama filtresi
        if (searchTerm) {
            filtered = filtered.filter(movie =>
                movie.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                movie.genre.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Tür filtresi
        if (selectedGenre) {
            filtered = filtered.filter(movie => movie.genre === selectedGenre);
        }

        // Sıralama
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    const dateA = new Date(a.addedToFavoritesAt || a.watchedAt);
                    const dateB = new Date(b.addedToFavoritesAt || b.watchedAt);
                    return dateB - dateA;
                case 'oldest':
                    const dateA2 = new Date(a.addedToFavoritesAt || a.watchedAt);
                    const dateB2 = new Date(b.addedToFavoritesAt || b.watchedAt);
                    return dateA2 - dateB2;
                case 'title':
                    return a.title.localeCompare(b.title);
                case 'rating':
                    return (b.rating || 0) - (a.rating || 0);
                default:
                    return 0;
            }
        });

        return filtered;
    };

    // Mevcut türleri al
    const getAvailableGenres = (movies) => {
        const genres = [...new Set(movies.map(movie => movie.genre))];
        return genres.sort();
    };

    // Film kartı komponenti
    const MovieCard = ({ movie, type }) => (
        <div
            onClick={() => handleMovieClick(movie)}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-200 border border-gray-200 dark:border-gray-700 cursor-pointer"
        >
            {/* Poster */}
            <div className="relative">
                <img
                    src={movie.posterUrl || posterUrls[movie.id] || '/placeholder-poster.jpg'}
                    alt={movie.title}
                    className="w-full h-64 object-cover"
                    onError={(e) => {
                        console.error(`❌ Poster yükleme hatası (FavoritesPage): ${movie.title} - URL: ${movie.posterUrl || posterUrls[movie.id]}`);
                        e.target.src = '/placeholder-poster.jpg';
                    }}
                />

                {/* Rating Badge */}
                <div className="absolute top-3 right-3 bg-black bg-opacity-80 text-white px-2 py-1 rounded-full">
                    <span className="font-bold text-sm">🌟 {movie.rating}</span>
                </div>

                {/* Durum İkonları */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {isFavorite(movie.id, movie.title) && (
                        <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                            <Heart className="h-4 w-4 fill-current" />
                        </div>
                    )}
                    {isWatched(movie.id) && (
                        <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                            <Eye className="h-4 w-4 fill-current" />
                        </div>
                    )}
                </div>
            </div>

            {/* İçerik */}
            <div className="p-4">
                <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-2 line-clamp-2">
                    {movie.title}
                </h3>

                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{movie.year}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Star className="h-4 w-4" />
                        <span>{movie.genre}</span>
                    </div>
                    {movie.actors && (
                        <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4" />
                            <span className="line-clamp-1">{movie.actors.slice(0, 2).join(', ')}</span>
                        </div>
                    )}
                    <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>
                            {type === 'favorite' && movie.addedToFavoritesAt &&
                                `${t('favorites.addedAt')}: ${formatDate(movie.addedToFavoritesAt)}`
                            }
                            {type === 'watched' && movie.watchedAt &&
                                `${t('favorites.watchedAt')}: ${formatDate(movie.watchedAt)}`
                            }
                        </span>
                    </div>
                </div>

                {/* Favori ve İzleme Toggle Butonları */}
                <div className="mt-4 space-y-2">
                    {/* İlk sıra: Favori ve İzleme */}
                    <div className="flex gap-2">
                        {/* Favori Toggle */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // Modal açılmasını engelle
                                handleFavoriteToggle(movie);
                            }}
                            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-red-50'
                                }`}
                        >
                            <Heart className={`h-3 w-3 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                            <span>{isFavorite(movie.id, movie.title) ? t('favorites.remove') : t('favorites.favorite')}</span>
                        </button>

                        {/* İzleme Durumu Toggle */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // Modal açılmasını engelle
                                handleWatchedToggle(movie);
                            }}
                            className={`flex-1 flex items-center justify-center space-x-1 px-2 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:bg-green-50'
                                }`}
                        >
                            <Eye className={`h-3 w-3 ${isWatched(movie.id) ? 'fill-current' : ''}`} />
                            <span>{isWatched(movie.id) ? t('favorites.watched') : t('favorites.watch')}</span>
                        </button>
                    </div>

                    {/* İkinci sıra: Fragman ve Google Ara */}
                    <div className="flex gap-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // Modal açılmasını engelle
                                handleWatchTrailer(movie);
                            }}
                            disabled={trailerLoading && currentMovieForTrailer === movie.title}
                            className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                        >
                            {trailerLoading && currentMovieForTrailer === movie.title ? (
                                <Loader className="h-3 w-3 animate-spin" />
                            ) : (
                                <Play className="h-3 w-3" />
                            )}
                            <span>
                                {trailerLoading && currentMovieForTrailer === movie.title
                                    ? t('favorites.searching')
                                    : t('favorites.trailer')
                                }
                            </span>
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openGoogleSearch(movie.title || movie.name);
                            }}
                            className="flex-1 flex items-center justify-center space-x-1 px-2 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                        >
                            <Search className="h-3 w-3" />
                            <span>{t('favorites.search')}</span>
                        </button>
                    </div>
                </div>

                {/* Tıklama İpucu */}
                <div className="mt-3 text-center">
                    <span className="text-xs text-purple-600 dark:text-purple-400 opacity-70">
                        {t('favorites.clickForDetails')}
                    </span>
                </div>
            </div>
        </div>
    );

    // Film açıklamasını parse etme fonksiyonu (geçmişten farklı olabilir)
    // NOT: posterUrl artık parse edilmiyor - movie.posterUrl kullanılıyor
    const parseMovieDescription = (description) => {
        if (!description) return {};

        const lines = description.split('\n');
        const parsed = {};

        lines.forEach(line => {
            if (line.includes('🎬 Film Adı:')) {
                parsed.title = line.replace('🎬 Film Adı:', '').trim();
            } else if (line.includes('📆 Yıl:')) {
                parsed.year = line.replace('📆 Yıl:', '').trim();
            } else if (line.includes('🌟 IMDb Puanı:')) {
                parsed.rating = line.replace('🌟 IMDb Puanı:', '').trim();
            } else if (line.includes('📝 Kısa Açıklama:')) {
                parsed.explanation = line.replace('📝 Kısa Açıklama:', '').trim();
            }
            // posterUrl artık parse edilmiyor - movie.posterUrl kullanılıyor
            // Bu, yanlış poster URL'lerinin description'dan gelmesini önler
        });

        return parsed;
    };

    // Detay Modal Komponenti
    const MovieDetailModal = ({ movie, onClose }) => {
        const [enhancedMovie, setEnhancedMovie] = useState(movie);
        const [plotLoading, setPlotLoading] = useState(false);
        const [castLoading, setCastLoading] = useState(false);

        // Eğer plot veya cast eksikse TMDB'den çek
        useEffect(() => {
            if (!movie) return;

            // Plot veya cast eksikse TMDB'den çek
            const needsPlot = !movie.plot;
            const needsCast = !movie.actors || movie.actors.length < 3 || (movie.actors.length === 3 && movie.actors[0] === 'Bilinmeyen');

            if (needsPlot || needsCast) {
                const loadMovieDetails = async () => {
                    if (needsPlot) setPlotLoading(true);
                    if (needsCast) setCastLoading(true);

                    try {
                        const contentType = movie.contentType || 'film';
                        const movieDetails = await getMovieDetailsWithCast(movie.title, movie.year, contentType);
                        
                        if (movieDetails) {
                            const updates = {};
                            
                            // Plot yükle
                            if (needsPlot) {
                                const plot = movieDetails?.overview || movieDetails?.plot;
                                if (plot) updates.plot = plot;
                            }
                            
                            // Cast yükle
                            if (needsCast && movieDetails?.cast && movieDetails.cast.length > 0) {
                                // Cast'i string array'e çevir (eğer object ise)
                                const castArray = movieDetails.cast.map(actor => 
                                    typeof actor === 'string' ? actor : actor.name
                                ).filter(Boolean);
                                
                                if (castArray.length > 0) {
                                    updates.actors = castArray;
                                }
                            }
                            
                            if (Object.keys(updates).length > 0) {
                                setEnhancedMovie(prev => ({
                                    ...prev,
                                    ...updates
                                }));
                            }
                        }
                    } catch (error) {
                        console.error('❌ Film detayları yükleme hatası:', error);
                    } finally {
                        if (needsPlot) setPlotLoading(false);
                        if (needsCast) setCastLoading(false);
                    }
                };

                loadMovieDetails();
            } else {
                setEnhancedMovie(movie);
            }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [movie?.id, movie?.title, movie?.year]);

        if (!movie) return null;

        const parsedInfo = parseMovieDescription(movie.description);

        // Modal dışına tıklandığında kapatma fonksiyonu
        const handleOverlayClick = (e) => {
            if (e.target === e.currentTarget) {
                onClose();
            }
        };

        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                onClick={handleOverlayClick}
            >
                <div
                    className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all"
                    >
                        <X className="h-6 w-6" />
                    </button>

                    {/* Main Content - Horizontal Layout */}
                    <div className="flex flex-col lg:flex-row">
                        {/* Left Side - Poster */}
                        <div className="lg:w-1/3 p-6">
                            <div className="relative aspect-[2/3] bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600 rounded-xl overflow-hidden shadow-xl">
                                {movie.posterUrl && movie.posterUrl !== 'https://via.placeholder.com/400x600/6366f1/ffffff?text=Film+Posteri' ? (
                                    <img
                                        src={movie.posterUrl}
                                        alt={movie.title}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-white bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700">
                                        <div className="text-center p-4">
                                            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                                                <Film className="h-20 w-20 mx-auto mb-4 opacity-90" />
                                                <p className="text-xl font-bold mb-2">{movie.title}</p>
                                                <p className="text-sm opacity-80">{parsedInfo.year || movie.year}</p>
                                                <div className="mt-3 flex items-center justify-center space-x-1">
                                                    <span className="text-yellow-300 text-lg">⭐</span>
                                                    <span className="text-base font-medium">{parsedInfo.rating || movie.rating}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rating Badge on Poster */}
                                <div className="absolute top-3 right-3 bg-black bg-opacity-80 text-white px-3 py-1 rounded-full">
                                    <span className="font-bold text-sm">🌟 {parsedInfo.rating || movie.rating}</span>
                                </div>

                                {/* Durum Göstergeleri */}
                                <div className="absolute top-3 left-3 flex flex-col gap-2">
                                    {isFavorite(movie.id, movie.title) && (
                                        <div className="bg-red-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Heart className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                    {isWatched(movie.id) && (
                                        <div className="bg-green-500 bg-opacity-90 text-white p-2 rounded-full shadow-lg">
                                            <Eye className="h-4 w-4 fill-current" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Movie Info */}
                        <div className="lg:w-2/3 p-6 lg:pl-0">
                            {/* Movie Title */}
                            <div className="mb-6">
                                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                                    🎬 {parsedInfo.title || movie.title}
                                </h1>
                            </div>

                            {/* Movie Stats */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                                        {parsedInfo.rating || movie.rating}
                                    </div>
                                    <div className="text-sm text-yellow-600 dark:text-yellow-500 font-medium">IMDb Puanı</div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                        {parsedInfo.year || movie.year}
                                    </div>
                                    <div className="text-sm text-blue-600 dark:text-blue-500 font-medium">{t('movie.productionYear')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-xl text-center">
                                    <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                                        {movie.duration || '120'}
                                    </div>
                                    <div className="text-sm text-green-600 dark:text-green-500 font-medium">{t('movie.minutes')}</div>
                                </div>
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-xl text-center">
                                    <div className="text-lg font-bold text-purple-700 dark:text-purple-400">
                                        {movie.genre}
                                    </div>
                                    <div className="text-sm text-purple-600 dark:text-purple-500 font-medium">{t('movie.genre')}</div>
                                </div>
                            </div>

                            {/* Cast */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                                    🎭 <span className="ml-2">{t('movie.actors')}</span>
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {castLoading ? (
                                        <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-sm italic">
                                            {t('common.loading')}...
                                        </span>
                                    ) : (enhancedMovie?.actors && enhancedMovie.actors.length > 0 && enhancedMovie.actors[0] !== 'Bilinmeyen') ? (
                                        enhancedMovie.actors.map((actor, index) => (
                                            <span key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                                {typeof actor === 'string' ? actor : actor.name}
                                            </span>
                                        ))
                                    ) : (movie.actors && movie.actors.length > 0 && movie.actors[0] !== 'Bilinmeyen') ? (
                                        movie.actors.map((actor, index) => (
                                            <span key={index} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                                {typeof actor === 'string' ? actor : actor.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg text-sm italic">
                                            {t('movie.actors')} {t('common.loading')}...
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Actions, Plot and Recommendation */}
                    <div className="px-6 pb-6 space-y-6">
                        {/* Favori ve İzleme Butonları */}
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center flex-wrap">
                            <button
                                onClick={() => handleFavoriteToggle(movie)}
                                className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isFavorite(movie.id, movie.title)
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-2 border-red-200 dark:border-red-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                                    }`}
                            >
                                <Heart className={`h-5 w-5 ${isFavorite(movie.id, movie.title) ? 'fill-current' : ''}`} />
                                <span className="hidden sm:inline">{isFavorite(movie.id, movie.title) ? t('favorites.removeFromFavorites') : t('favorites.addToFavorites')}</span>
                                <span className="sm:hidden">{isFavorite(movie.id, movie.title) ? t('favorites.remove') : t('favorites.favorite')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchedToggle(movie)}
                                className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 ${isWatched(movie.id)
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-2 border-green-200 dark:border-green-700'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                                    }`}
                            >
                                {isWatched(movie.id) ? (
                                    <Eye className="h-5 w-5 fill-current" />
                                ) : (
                                    <EyeOff className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">{isWatched(movie.id) ? `${t('favorites.watched')} ✓` : t('favorites.markAsWatched')}</span>
                                <span className="sm:hidden">{isWatched(movie.id) ? t('favorites.watched') : t('favorites.watch')}</span>
                            </button>

                            <button
                                onClick={() => handleWatchTrailer(movie)}
                                disabled={trailerLoading && currentMovieForTrailer === movie.title}
                                className="flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50"
                            >
                                {trailerLoading && currentMovieForTrailer === movie.title ? (
                                    <Loader className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Play className="h-5 w-5" />
                                )}
                                <span className="hidden sm:inline">
                                    {trailerLoading && currentMovieForTrailer === movie.title
                                        ? t('favorites.trailerSearching')
                                        : t('favorites.watchTrailer')
                                    }
                                </span>
                                <span className="sm:hidden">
                                    {trailerLoading && currentMovieForTrailer === movie.title
                                        ? t('favorites.searching')
                                        : t('favorites.trailer')
                                    }
                                </span>
                            </button>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    openGoogleSearch(movie.title || movie.name);
                                }}
                                className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium transition-all transform hover:scale-105 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-700 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                            >
                                <Search className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('favorites.searchOnGoogle')}</span>
                            </button>
                        </div>

                        {/* Plot */}
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                📖 <span className="ml-2">{t('favorites.moviePlot')}</span>
                            </h3>
                            <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-6">
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                    {plotLoading ? (
                                        <span className="text-gray-500 dark:text-gray-400">{t('common.loading')}...</span>
                                    ) : (
                                        enhancedMovie?.plot || movie.plot || t('favorites.noPlotAvailable')
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Why Recommended - Favoriler sayfasında bu bölüm özelleştirildi */}
                        {(movie.addedToFavoritesAt || movie.watchedAt) && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    💡 <span className="ml-2">{t('favorites.myCollection')}</span>
                                </h3>
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-6 border-l-4 border-purple-500">
                                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                                        {movie.addedToFavoritesAt && `${t('favorites.addedAt')}: ${formatDate(movie.addedToFavoritesAt)}`}
                                        {movie.addedToFavoritesAt && movie.watchedAt && ' • '}
                                        {movie.watchedAt && `${t('favorites.watchedAt')}: ${formatDate(movie.watchedAt)}`}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            <div className="text-center mb-6 sm:mb-8">
                <div className="flex justify-center items-center space-x-2 mb-4">
                    <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('favorites.myFavoriteMovies')}</h1>
                </div>
                {favorites.length > 0 ? (
                    <p className="text-gray-600 dark:text-gray-400 px-4">
                        {t('favorites.youHaveFavorites', { count: favorites.length })}
                    </p>
                ) : (
                    <p className="text-gray-600 dark:text-gray-400 px-4">
                        {t('favorites.noFavoritesYet')}
                    </p>
                )}
            </div>

            {/* Tab Butonları */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-6 sm:mb-8">
                <button
                    onClick={() => setActiveTab('favorites')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium transition-all ${activeTab === 'favorites'
                        ? 'bg-red-500 text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                >
                    <Heart className={`h-4 w-4 sm:h-5 sm:w-5 ${activeTab === 'favorites' ? 'fill-current' : ''}`} />
                    <span>{t('favorites.myFavorites')} ({favorites.length})</span>
                </button>
                <button
                    onClick={() => setActiveTab('watched')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium transition-all ${activeTab === 'watched'
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                >
                    <Eye className={`h-4 w-4 sm:h-5 sm:w-5 ${activeTab === 'watched' ? 'fill-current' : ''}`} />
                    <span>{t('favorites.watchedMovies')} ({watchedMovies.length})</span>
                </button>
                <button
                    onClick={() => setActiveTab('stats')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium transition-all ${activeTab === 'stats'
                        ? 'bg-purple-500 text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                        }`}
                >
                    <BarChart3 className={`h-4 w-4 sm:h-5 sm:w-5 ${activeTab === 'stats' ? 'fill-current' : ''}`} />
                    <span className="hidden sm:inline">{t('favorites.statistics')}</span>
                    <span className="sm:hidden">{t('favorites.statisticsShort')}</span>
                </button>
            </div>

            {/* İçerik */}
            {(activeTab === 'favorites' || activeTab === 'watched') && (
                <>
                    {/* Filtreler ve Arama */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-6 sm:mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('favorites.filterAndSearch')}</h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                            {/* Arama */}
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('favorites.searchMovie')}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                            />

                            {/* Tür filtresi */}
                            <select
                                value={selectedGenre}
                                onChange={(e) => setSelectedGenre(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                            >
                                <option value="">{t('favorites.allGenres')}</option>
                                {getAvailableGenres(activeTab === 'favorites' ? favorites : watchedMovies).map(genre => (
                                    <option key={genre} value={genre}>{genre}</option>
                                ))}
                            </select>

                            {/* Sıralama */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
                            >
                                <option value="newest">{t('favorites.newest')}</option>
                                <option value="oldest">{t('favorites.oldest')}</option>
                                <option value="title">{t('favorites.byTitle')}</option>
                                <option value="rating">{t('favorites.byRating')}</option>
                            </select>

                            {/* Temizle */}
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedGenre('');
                                    setSortBy('newest');
                                }}
                                className="px-3 sm:px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm sm:text-base"
                            >
                                {t('common.clear')}
                            </button>
                        </div>
                    </div>

                    {/* Film Listesi */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                        {(() => {
                            const currentMovies = activeTab === 'favorites' ? favorites : watchedMovies;
                            const filteredMovies = filterAndSortMovies(currentMovies);

                            if (filteredMovies.length === 0) {
                                return (
                                    <div className="col-span-full text-center py-12">
                                        <div className="text-gray-400 dark:text-gray-500 mb-4">
                                            {activeTab === 'favorites' ? (
                                                <Heart className="h-16 w-16 mx-auto mb-4" />
                                            ) : (
                                                <Eye className="h-16 w-16 mx-auto mb-4" />
                                            )}
                                        </div>
                                        <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                                            {currentMovies.length === 0 ? (
                                                activeTab === 'favorites' ? t('favorites.noFavorites') : t('favorites.noWatchedMovies')
                                            ) : (
                                                t('favorites.noMoviesFound')
                                            )}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {currentMovies.length === 0 ? (
                                                t('favorites.addFromRecommendations')
                                            ) : (
                                                t('favorites.tryDifferentFilters')
                                            )}
                                        </p>
                                    </div>
                                );
                            }

                            return filteredMovies.map(movie => (
                                <MovieCard
                                    key={`${activeTab}-${movie.id}`}
                                    movie={movie}
                                    type={activeTab === 'favorites' ? 'favorite' : 'watched'}
                                />
                            ));
                        })()}
                    </div>
                </>
            )}

            {/* İstatistikler */}
            {activeTab === 'stats' && (
                <div className="space-y-6">
                    {/* Genel İstatistikler */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-600 dark:text-red-400 text-sm font-medium">{t('dashboard.totalFavorites')}</p>
                                    <p className="text-3xl font-bold text-red-700 dark:text-red-300">{stats.totalFavorites || 0}</p>
                                </div>
                                <Heart className="h-12 w-12 text-red-500" />
                            </div>
                        </div>

                        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-600 dark:text-green-400 text-sm font-medium">Toplam İzlenen</p>
                                    <p className="text-3xl font-bold text-green-700 dark:text-green-300">{stats.totalWatched || 0}</p>
                                </div>
                                <Eye className="h-12 w-12 text-green-500" />
                            </div>
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Bu Ay İzlenen</p>
                                    <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{stats.watchedThisMonth || 0}</p>
                                </div>
                                <Calendar className="h-12 w-12 text-blue-500" />
                            </div>
                        </div>

                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">En Çok Sevilen Tür</p>
                                    <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                                        {stats.favoriteGenres?.[0]?.genre || 'Henüz yok'}
                                    </p>
                                </div>
                                <BarChart3 className="h-12 w-12 text-purple-500" />
                            </div>
                        </div>
                    </div>

                    {/* Favori Türler */}
                    {stats.favoriteGenres && stats.favoriteGenres.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">En Çok Favorilenen Türler</h3>
                            <div className="space-y-3">
                                {stats.favoriteGenres.map((item, index) => (
                                    <div key={item.genre} className="flex items-center justify-between">
                                        <span className="text-gray-700 dark:text-gray-300">{index + 1}. {item.genre}</span>
                                        <div className="flex items-center space-x-2">
                                            <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                <div
                                                    className="bg-purple-500 h-2 rounded-full"
                                                    style={{
                                                        width: `${(item.count / stats.totalFavorites) * 100}%`
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-400">{item.count}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Son Eklenenler */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Son Favoriler */}
                        {stats.recentFavorites && stats.recentFavorites.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Heart className="h-5 w-5 text-red-500 mr-2" />
                                    Son Favoriler
                                </h3>
                                <div className="space-y-3">
                                    {stats.recentFavorites.map((movie) => (
                                        <div key={movie.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <img
                                                src={posterUrls[movie.id] || '/placeholder-poster.jpg'}
                                                alt={movie.title}
                                                className="w-12 h-16 object-cover rounded"
                                            />
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900 dark:text-white">{movie.title}</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{movie.genre} • {movie.year}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Son İzlenenler */}
                        {stats.recentWatched && stats.recentWatched.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Eye className="h-5 w-5 text-green-500 mr-2" />
                                    {t('favorites.recentWatched')}
                                </h3>
                                <div className="space-y-3">
                                    {stats.recentWatched.map((movie) => (
                                        <div key={movie.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <img
                                                src={posterUrls[movie.id] || '/placeholder-poster.jpg'}
                                                alt={movie.title}
                                                className="w-12 h-16 object-cover rounded"
                                            />
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-900 dark:text-white">{movie.title}</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{movie.genre} • {movie.year}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal */}
            {selectedMovie && (
                <MovieDetailModal
                    movie={selectedMovie}
                    onClose={closeModal}
                />
            )}

            {/* Trailer Modal */}
            <TrailerModal
                trailer={selectedTrailer}
                isOpen={isTrailerModalOpen}
                onClose={closeTrailerModal}
                movieTitle={currentMovieForTrailer}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                onClose={closeConfirmationModal}
                onConfirm={handleConfirmationConfirm}
                title={confirmationModal.title}
                message={confirmationModal.message}
                type={confirmationModal.type}
                confirmText={confirmationModal.confirmText}
                cancelText={confirmationModal.cancelText}
                isLoading={confirmationModal.isLoading}
            />
        </div>
    );
};

export default FavoritesPage; 